import * as React from "react";
import { createContext, useContext, useState, ReactNode } from "react";
import { styled } from "@mui/material/styles";
import { Box, BoxProps } from "@mui/material";
import { FONT_SIZES, FONT_WEIGHTS } from "../../constants/styles/typography";

// Tabs Context
interface TabsContextType {
  selectedValue: string | number;
  onValueChange: (value: string | number) => void;
  size: "small" | "medium" | "large";
}

const TabsContext = createContext<TabsContextType | undefined>(undefined);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tabs.Item must be used within Tabs.Container");
  }
  return context;
};

/**
 * 탭 컨테이너 props — `Tabs.Container` 와 `Tabs.Item` 의 부모 컴포넌트.
 * 자식의 `value` 와 같은 선택 상태를 Context 로 공유한다 — 단독 사용 불가.
 */
export interface TabsContainerProps extends Omit<BoxProps, "onChange"> {
  value?: string | number;
  defaultValue?: string | number;
  onValueChange?: (value: string | number) => void;
  size?: "small" | "medium" | "large";
  children: ReactNode;
  /** 테스트 앵커. 루트로 전파된다(`...props`). 값 규약은 `src/test/testIds.ts` 참조. */
  "data-testid"?: string;
}

/**
 * 개별 탭 아이템 props — 반드시 `Tabs.Container` 자식으로 사용해야 한다.
 */
export interface TabsItemProps extends Omit<BoxProps, "onClick"> {
  value: string | number;
  children: ReactNode;
  disabled?: boolean;
  /** 테스트 앵커. 루트로 전파된다(`...props`). 값 규약은 `src/test/testIds.ts` 참조. */
  "data-testid"?: string;
}

// Styled Components
const StyledTabsContainer = styled(Box)<{ size: "small" | "medium" | "large" }>(
  ({ theme, size }) => {
    const sizeConfig = {
      small: {
        padding: "4px",
        borderRadius: "8px",
        gap: "4px",
      },
      medium: {
        padding: "4px",
        borderRadius: "8px",
        gap: "4px",
      },
      large: {
        padding: "4px",
        borderRadius: "8px",
        gap: "4px",
      },
    };

    const config = sizeConfig[size];

    return {
      display: "inline-flex",
      alignItems: "center",
      backgroundColor: theme.palette.grey[200],
      padding: config.padding,
      borderRadius: config.borderRadius,
      gap: config.gap,
      minHeight:
        size === "small" ? "34px" : size === "medium" ? "36px" : "38px",
    };
  }
);

const StyledTabsItem = styled(Box)<{
  selected: boolean;
  disabled: boolean;
  size: "small" | "medium" | "large";
}>(({ theme, selected, disabled, size }) => {
  const sizeConfig = {
    small: {
      height: "26px",
      padding: "0 8px",
      fontSize: FONT_SIZES.xs,
      fontWeight: FONT_WEIGHTS.medium,
      borderRadius: "4px",
    },
    medium: {
      height: "28px",
      padding: "0 10px",
      fontSize: FONT_SIZES.sm,
      fontWeight: FONT_WEIGHTS.medium,
      borderRadius: "4px",
    },
    large: {
      height: "30px",
      padding: "0 12px",
      fontSize: "15px",
      fontWeight: FONT_WEIGHTS.semiBold,
      borderRadius: "4px",
    },
  };

  const config = sizeConfig[size];

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: config.height,
    padding: config.padding,
    fontSize: config.fontSize,
    fontWeight: config.fontWeight,
    borderRadius: config.borderRadius,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease-in-out",
    userSelect: "none",
    whiteSpace: "nowrap",
    minWidth: "fit-content",

    // Selected state
    ...(selected && {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text.primary,
      boxShadow:
        "0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 2px 1px -1px rgba(0, 0, 0, 0.20)",
    }),

    // Unselected state
    ...(!selected && {
      backgroundColor: "transparent",
      color: theme.palette.text.secondary,
      opacity: 0.6,
    }),

    // Disabled state
    ...(disabled && {
      opacity: 0.4,
      cursor: "not-allowed",
    }),

    // Hover state (only when not disabled)
    ...(!disabled && {
      "&:hover": {
        opacity: selected ? 1 : 0.8,
      },
    }),

    // Focus state
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: "2px",
    },
  };
});

// Tabs Container Component
export const TabsContainer = React.forwardRef<
  HTMLDivElement,
  TabsContainerProps
>(
  (
    { value, defaultValue, onValueChange, size = "medium", children, ...props },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState<string | number>(
      defaultValue || ""
    );

    const selectedValue = value !== undefined ? value : internalValue;

    const handleValueChange = (newValue: string | number) => {
      if (value === undefined) {
        setInternalValue(newValue);
      }
      onValueChange?.(newValue);
    };

    const contextValue: TabsContextType = {
      selectedValue,
      onValueChange: handleValueChange,
      size,
    };

    return (
      <TabsContext.Provider value={contextValue}>
        <StyledTabsContainer ref={ref} size={size} {...props}>
          {children}
        </StyledTabsContainer>
      </TabsContext.Provider>
    );
  }
);

TabsContainer.displayName = "TabsContainer";

// Tabs Item Component
export const TabsItem = React.forwardRef<HTMLDivElement, TabsItemProps>(
  ({ value, children, disabled = false, ...props }, ref) => {
    const { selectedValue, onValueChange, size } = useTabsContext();
    const isSelected = selectedValue === value;

    const handleClick = () => {
      if (!disabled) {
        onValueChange(value);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
    };

    return (
      <StyledTabsItem
        ref={ref}
        selected={isSelected}
        disabled={disabled}
        size={size}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="tab"
        aria-selected={isSelected}
        aria-disabled={disabled}
        {...props}
      >
        {children}
      </StyledTabsItem>
    );
  }
);

TabsItem.displayName = "TabsItem";

// Compound Component
export const Tabs = {
  Container: TabsContainer,
  Item: TabsItem,
};

export default Tabs;
